import { useState, useEffect, useCallback } from "react";
import { type WalletClient, type TransactionRequest, maxUint256 } from "viem";
import { useWalletClient } from "wagmi";
import { estimateMulticallGas } from "@/utils";
import { getGatewayContractAddress } from "@/config";
import { getTokenAddress } from "@/config";
import type { TokenId } from "@/types";
import { prepareTokenApprovalTransaction } from "@/services";

type GasEstimationFunction = (walletClient: WalletClient) => Promise<bigint>;
type TransactionRequestFunction = (walletClient: WalletClient) => Promise<TransactionRequest>;

interface UseGasEstimationProps {
    estimateGasFn: GasEstimationFunction;
    prepareTransactionFn: TransactionRequestFunction;
    hasApproval?: boolean;
    tokenId?: TokenId;
}

export function useGasEstimation({
    estimateGasFn,
    prepareTransactionFn,
    hasApproval = false,
    tokenId,
}: UseGasEstimationProps) {
    const [gasEstimate, setGasEstimate] = useState<bigint | null>(null);
    const [isEstimatingGas, setIsEstimatingGas] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const { data: walletClient } = useWalletClient();

    // Memoize the core estimation logic
    const performGasEstimation = useCallback<(client: WalletClient) => Promise<bigint>>(
        async (client: WalletClient) => {
            if (!client.chain?.id) {
                throw new Error("Chain ID not available");
            }

            if (!hasApproval && tokenId) {
                const tokenAddress = getTokenAddress(client.chain.id, tokenId);
                const spenderAddress = getGatewayContractAddress(client.chain.id);

                const approvalTx = prepareTokenApprovalTransaction(tokenAddress, spenderAddress, maxUint256);
                const mainTx = await prepareTransactionFn(client);

                return await estimateMulticallGas(client, [approvalTx, mainTx]);
            }

            return await estimateGasFn(client);
        },
        [hasApproval, tokenId, prepareTransactionFn, estimateGasFn],
    );

    useEffect(() => {
        const estimate = async () => {
            if (!walletClient) return;

            setIsEstimatingGas(true);
            setError(null);

            try {
                const estimate = await performGasEstimation(walletClient);
                setGasEstimate(estimate);
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : "Failed to estimate gas";
                setError(new Error(errorMessage));
                console.error("Gas estimation error:", err);
            } finally {
                setIsEstimatingGas(false);
            }
        };

        estimate();
    }, [walletClient, performGasEstimation]);

    const estimateGas = useCallback(() => {
        // Causes re-estimation of gas via useEffect
        setGasEstimate(null);
        setError(null);
        setIsEstimatingGas(true);
    }, [setGasEstimate, setError, setIsEstimatingGas]);

    return {
        gasEstimate,
        isEstimatingGas,
        error,
        estimateGas,
    };
}
